import { StateCreator } from 'zustand';
import { Node, NodeChange, applyNodeChanges, XYPosition } from '@xyflow/react';
import { PipelineSlice } from './pipelineSlice';

export interface NodeSlice {
  // Node operations
  setNodes: (nodes: Node[]) => void;
  onNodesChange: (changes: NodeChange[]) => void;
  addNode: (node: Node) => void;
  updateNode: (pipelineId: string, nodeId: string, updates: Partial<Node>) => void;
  deleteNode: (nodeId: string) => void;
  updateNodeData: (nodeId: string, data: Record<string, unknown>) => void;
  setSelectedNodeId: (nodeId: string | null, isDoubleClicked?: boolean) => void;
  setLastNodePosition: (position: XYPosition) => void;
  clearNodeSelectionState: () => void;
}

export const createNodeSlice: StateCreator<
  PipelineSlice & NodeSlice,
  [],
  [],
  NodeSlice
> = (set) => ({
  setNodes: (nodes) =>
    set((state) => {
      if (!state.activePipelineId) return state;
      return {
        pipelines: state.pipelines.map((p) =>
          p.id === state.activePipelineId ? { ...p, nodes } : p
        ),
      };
    }),

  onNodesChange: (changes) =>
    set((state) => {
      if (!state.activePipelineId) return state;
      const pipeline = state.pipelines.find((p) => p.id === state.activePipelineId);
      if (!pipeline) return state;
      return {
        pipelines: state.pipelines.map((p) =>
          p.id === state.activePipelineId
            ? { ...p, nodes: applyNodeChanges(changes, p.nodes) }
            : p
        ),
      };
    }),

  addNode: (node) =>
    set((state) => {
      if (!state.activePipelineId) return state;
      const pipeline = state.pipelines.find((p) => p.id === state.activePipelineId);
      if (!pipeline) return state;
      return {
        pipelines: state.pipelines.map((p) =>
          p.id === state.activePipelineId 
            ? { 
                ...p, 
                nodes: [...p.nodes, node],
                lastNodePosition: node.position, // Track position for next node placement
              } 
            : p
        ),
      };
    }),

  updateNode: (pipelineId, nodeId, updates) =>
    set((state) => {
      const pipeline = state.pipelines.find((p) => p.id === pipelineId);
      if (!pipeline) return state;
      return {
        pipelines: state.pipelines.map((p) =>
          p.id === pipelineId
            ? {
                ...p,
                nodes: p.nodes.map((n) =>
                  n.id === nodeId ? { ...n, ...updates } : n
                ),
              }
            : p
        ),
      };
    }),

  deleteNode: (nodeId) =>
    set((state) => {
      if (!state.activePipelineId) return state;
      const pipeline = state.pipelines.find((p) => p.id === state.activePipelineId);
      if (!pipeline) return state;
      return {
        pipelines: state.pipelines.map((p) =>
          p.id === state.activePipelineId
            ? {
                ...p,
                nodes: p.nodes.filter((n) => n.id !== nodeId),
                edges: p.edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
              }
            : p
        ),
      };
    }),

  updateNodeData: (nodeId, data) =>
    set((state) => {
      if (!state.activePipelineId) return state;
      const pipeline = state.pipelines.find((p) => p.id === state.activePipelineId);
      if (!pipeline) return state;
      return {
        pipelines: state.pipelines.map((p) =>
          p.id === state.activePipelineId
            ? {
                ...p,
                nodes: p.nodes.map((n) =>
                  n.id === nodeId ? { ...n, data: { ...n.data, ...data } } : n
                ),
              }
            : p
        ),
      };
    }),

  setSelectedNodeId: (nodeId, isDoubleClicked = false) =>
    set((state) => {
      if (!state.activePipelineId) return state;
      return {
        pipelines: state.pipelines.map((p) =>
          p.id === state.activePipelineId 
            ? { ...p, selectedNodeId: nodeId, isNodeDoubleClicked: isDoubleClicked } 
            : p
        ),
      };
    }),

  setLastNodePosition: (position) =>
    set((state) => {
      if (!state.activePipelineId) return state;
      return {
        pipelines: state.pipelines.map((p) =>
          p.id === state.activePipelineId ? { ...p, lastNodePosition: position } : p
        ),
      };
    }),

  clearNodeSelectionState: () =>
    set((state) => {
      if (!state.activePipelineId) return state;
      return {
        pipelines: state.pipelines.map((p) =>
          p.id === state.activePipelineId
            ? {
                ...p,
                selectedNodeId: null,
                isNodeDoubleClicked: false,
              }
            : p
        ),
      };
    }),
});

